import React, { Component } from 'react';
import { Flex, Button, TextField, RadioButton } from 'monday-ui-react-core';
import { Heading } from "monday-ui-react-core/next";
import classes from "./board-view-backup-editor.module.scss";
import { showNotice } from '../../services/error-handling';
import { valueCreated } from '../../services/hooks';
import mondaySdk from "monday-sdk-js";
import { createNewBackup } from '../../services/backend-service';
import { getBoardGroups, getItem } from '../../services/api-service';

const FEATURE_ITEM_VIEW = "AppFeatureItemMenuAction";
const FEATURE_ITEMS_VIEW = "AppFeatureItemBatchAction";
const FEATURE_GROUP_VIEW = "AppFeatureGroupMenuAction";

class BoardViewBackupEditor extends Component {
  constructor(props) {
    super(props);
    this.state = {
      board: null,
      item: null,
      appFeature: null,
      boardId: null,
      pulseId: null,
      groupId: null,
      groupName: null,
      selectedPulsesIds: null,
      backupName: "",
      backupVisibility: "private",
      backupDescription: "",
      userId: null,
      loadingOnCreation: false,
      operationComplete: false,
      operationCompleteMessage: ""
    };
  }

  componentDidMount() {
    const monday = mondaySdk();

    const initView = async (data) => {
      const board = await getBoardGroups(data.boardId);
      let item = null;
      let itemName = "";
      let groupName = null;
      if(data.appFeature.type === FEATURE_ITEM_VIEW) {
        item = await getItem(data.pulseId);
        itemName = item.name + " [" + board.name + "]";
      } else if(data.appFeature.type === FEATURE_ITEMS_VIEW) {
        itemName = "Multiple items [" + board.name + "]";
      } else if(data.appFeature.type === FEATURE_GROUP_VIEW) {
        groupName = "";
        board.groups.forEach((group) => {
          if(group.id === data.groupId) {
            groupName = group.title;
          }
        });

        itemName = groupName + " [" + board.name + "]";
      }

      this.generateBackupName(itemName);

      this.setState({
        board: board,
        item: item,
        groupName: groupName
      });
    }

    monday.listen("context", (res) => {
      this.setState({
        isAdmin: res.data.user.isAdmin,
        isGuest: res.data.user.isGuest,
        isViewOnly: res.data.user.isViewOnly,
        userId: res.data.user.id,
        timeZoneOffset: res.data.user.timeZoneOffset,
        appFeature: res.data.appFeature.type,
        boardId: res.data.boardId,
        pulseId: res.data.pulseId,
        groupId: res.data.groupId,
        selectedPulsesIds: res.data.selectedPulsesIds
      });

      initView(res.data);
    });
  }

  async save() {
    if(this.validateBackupName(this.state.backupName).status === "error") {
      showNotice("error", "Please correct the validation errors");
      return;
    }

    this.setState({ 
      loadingOnCreation: true
    });

    let backup = {
      boardId: this.state.boardId,
      backupName: this.state.backupName,
      workspaceId: this.state.board.workspace_id,
      ownerId: this.state.userId,
      backupVisibility: this.state.backupVisibility,
      metadata: { 
        description: this.state.backupDescription
      }
    }

    let operationCompleteMessage = "";
    if(this.state.appFeature === FEATURE_ITEM_VIEW) {
      backup.backupType = "items";
      backup.boardName = this.state.item.name  + " [" + this.state.board.name + "]";
      backup.metadata.backupItemIds = [this.state.pulseId];
      operationCompleteMessage = "Snapshot created successfully! You may access it through the Board & Doc Backups workspace app.";
    } else if(this.state.appFeature === FEATURE_ITEMS_VIEW) {
      backup.backupType = "items";
      backup.boardName = "Multiple items [" + this.state.board.name + "]";
      backup.metadata.backupItemIds = this.state.selectedPulsesIds;
      operationCompleteMessage = "Snapshot creation in progress - it may take up to 10 minutes to finish. Please don't modify the selected items until the snapshot is complete. Once ready, it will become available through the Board & Doc Backups workspace app.";
    } else if(this.state.appFeature === FEATURE_GROUP_VIEW) {
      backup.backupType = "group";
      backup.boardName = this.state.groupName + " [" + this.state.board.name + "]";
      backup.metadata.backupBoardGroupId = this.state.groupId;
      operationCompleteMessage = "Snapshot created successfully! You may access it through the Board & Doc Backups workspace app.";
    }

    try {
      await createNewBackup(this.state.board.workspace_id, backup);
      valueCreated();
      this.setState({ 
        loadingOnCreation: false,
        operationComplete: true,
        operationCompleteMessage: operationCompleteMessage
      });
    } catch(error) {
      console.log(error);
      showNotice("error", "An error has occured while creating snapshot. Please try again.");
      this.setState({ 
        loadingOnCreation: false,
        operationComplete: true,
        operationCompleteMessage: "An error has occured while creating snapshot. Please try again."
      });
    }
  }

  validateBackupName(name) {
    if(!name || (name.length < 5)) {
      return {
        status: "error",
        text: "Snapshot name must be at least 5 characters"
      };
    }

    return {};
  }

  generateBackupName(itemName) {
    this.setState({
      backupName: itemName + " - " + this.formatDate(new Date()),
    });
  }

  formatDate(date) {
    // Define an array of month abbreviations
    const monthNames = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
  
    // Extract hours, minutes, and seconds, ensuring two digits with padStart()
    const hh = String(date.getHours()).padStart(2, '0');
    const mm = String(date.getMinutes()).padStart(2, '0');
    const ss = String(date.getSeconds()).padStart(2, '0');
  
    // Extract day (two digits) and get month name from the array
    const day = String(date.getDate()).padStart(2, '0');
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
  
    return `${hh}:${mm}:${ss} ${day} ${month} ${year}`;
  }

  render() {
    if(this.state.board?.workspace_id === -1 || this.state.board?.workspace_id === "-1" || this.state.board?.workspace_id === null) {
      return(
        <Flex align={Flex.align.START} gap={Flex.gaps.SMALL} direction={Flex.directions.ROW}>
          <Flex align={Flex.align.CENTER} gap={Flex.gaps.SMALL} direction={Flex.directions.COLUMN} className={classes.emptyStateFlex}>
            <div className={classes.emptyStateComponent}>
              <div className={classes.emptyStateContainer}>
                <img className={classes.permissionsImg} alt="Workspace restriction" src="/monday/bb/v1/config/images/empty-set.png" />
              </div>
              <div className={classes.ctaContainer}>
                <div className={classes.ctaContainerTitle}>Oops, not available here</div>
                <div className={classes.ctaContainerSubtitle}>This functionality isn't available in the current workspace. Please switch to a different workspace.</div>
              </div>
            </div>
          </Flex>
        </Flex>
      );
    }

    return (
      (!this.state.operationComplete)
      ? <Flex align={Flex.align.START} gap={Flex.gaps.SMALL} direction={Flex.directions.COLUMN} className={classes.container}>
          <Flex align={Flex.align.CENTER} gap={Flex.gaps.SMALL} direction={Flex.directions.ROW}>
            <Heading className={classes.sectionHeading} type={Heading.types.H2}>Create new backup snapshot</Heading>
          </Flex>
          <div className={classes.formTitle}>Snapshot visibility</div>
          <Flex align={Flex.align.CENTER} gap={Flex.gaps.SMALL} direction={Flex.directions.ROW} className={classes.fullWidth}>
              <RadioButton 
                text="Private" 
                name="visibility-group" 
                defaultChecked={this.state.backupVisibility === "private"} 
                onSelect={() => { this.setState({backupVisibility: "private"}) 
                }} />
              <RadioButton 
                text="Public" 
                name="visibility-group" 
                disabled={!this.state.board || (this.state.board?.board_kind === "private")}
                defaultChecked={this.state.backupVisibility === "public"} 
                onSelect={() => { this.setState({backupVisibility: "public"}) 
                }} />
          </Flex>
          <div className={classes.formTitle}>Snapshot name</div>
          <TextField 
            size={TextField.sizes.MEDIUM} 
            value={this.state.backupName} 
            maxLength={200}
            onChange={(value) => { 
              this.setState({ 
                backupName: value
              }); 
            }}
            validation={this.validateBackupName(this.state.backupName)} 
          />
          <div className={classes.formTitle}>Description</div>
          <TextField 
            size={TextField.sizes.MEDIUM} 
            value={this.state.backupDescription} 
            maxLength={500}
            onChange={(value) => { 
              this.setState({ 
                backupDescription: value
              }); 
            }}
          />
          <Flex align={Flex.align.CENTER} gap={Flex.gaps.SMALL} direction={Flex.directions.ROW}>
            <Button 
              size={Button.sizes.SMALL} 
              kind={Button.kinds.PRIMARY}
              loading={this.state.loadingOnCreation}
              disabled={this.state.loadingOnCreation}
              onClick={() => {
                this.save();
              }}
            >
              Create Snapshot
            </Button>
          </Flex>
        </Flex>
      : <Flex align={Flex.align.START} gap={Flex.gaps.SMALL} direction={Flex.directions.COLUMN} className={classes.container}>
        <div className={classes.formTitle}>{this.state.operationCompleteMessage}</div>
      </Flex>
    );
  }
}

export default BoardViewBackupEditor;
